import { useState } from "react";
import { Heart, Play, StickyNote } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "../lib/api";
import { accentByIndex, accentFor } from "../lib/accents";

export const AdCard = ({ ad, index, categories, onOpen, onFavoriteChange }) => {
  const [fav, setFav] = useState(ad.is_favorite);
  const accent = accentByIndex(index);
  const cats = (ad.category_ids || []).map((id) => categories.find((c) => c.id === id)).filter(Boolean);
  const noteCount = ad.note_count || 0;
  const hasNote = noteCount > 0;

  const toggleFav = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/favorites/${ad.id}`);
      setFav(data.is_favorite);
      onFavoriteChange?.(ad.id, data.is_favorite);
      toast.success(data.is_favorite ? "Saved to Favorites" : "Removed from Favorites");
    } catch (e2) {
      toast.error("Could not update favorite");
    }
  };

  return (
    <div
      data-testid={`ad-card-${ad.id}`}
      onClick={() => onOpen(ad)}
      className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-shadow duration-300 hover:-translate-y-1"
      style={{ transition: "box-shadow 0.3s ease, border-color 0.3s ease, transform 0.3s ease" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 0 28px ${accent.soft}, 0 8px 24px rgba(0,0,0,0.06)`;
        e.currentTarget.style.borderColor = accent.hex;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = "#E5E7EB";
      }}
    >
      <div className="relative bg-gray-50">
        <img src={mediaUrl(ad.id, "thumb")} alt={ad.title} loading="lazy" className="w-full block" />
        {ad.media_type === "video" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow-lg group-hover:scale-110" style={{ transition: "transform 0.25s ease" }}>
              <Play size={22} className="text-[#000000] ml-1" fill="#000000" />
            </div>
          </div>
        )}
        <button
          data-testid={`favorite-btn-${ad.id}`}
          onClick={toggleFav}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md hover:scale-110"
          style={{ transition: "transform 0.2s ease" }}
          aria-label="Toggle favorite"
        >
          <Heart size={17} className={fav ? "text-[#DF1283]" : "text-gray-500"} fill={fav ? "#DF1283" : "none"} />
        </button>
        {hasNote && (
          <div
            data-testid={`ad-card-note-indicator-${ad.id}`}
            className="absolute top-3 left-3 h-9 min-w-9 px-2 rounded-full bg-white/90 backdrop-blur flex items-center justify-center gap-1 shadow-md"
            title={`${noteCount} note${noteCount === 1 ? "" : "s"} on this ad`}
          >
            <StickyNote size={13} className="text-[#FC861E]" fill="#FC861E" fillOpacity={0.25} />
            {noteCount > 1 && <span className="text-[11px] font-bold text-[#000000]">{noteCount}</span>}
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-display font-bold text-sm text-[#000000] truncate" data-testid={`ad-card-title-${ad.id}`}>{ad.title}</h3>
        {cats.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {cats.slice(0, 3).map((c) => {
              const a = accentFor(c.color);
              return (
                <span key={c.id} data-testid={`ad-card-category-${c.id}`} className="px-2.5 py-0.5 rounded-full text-[11px] font-bold" style={{ background: a.hex, color: a.text }}>
                  {c.name}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
